import { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  FacephiSdkProvider,
  Language,
  TypeFamily,
  FacephiSdkProviderCustomEvent,
  ExportData,
  AssetsType,
  TrackingSignatureValue,
  WorkflowDTO,
} from '@facephi/sdk-web-react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useVariables } from '../hooks';
import { useAuth, useConfig } from '../providers';
import {
  newOperationIdState,
  operationIdState,
  sessionIdState,
} from '../states/atoms';

type IProps = {
  children: React.ReactNode;
};

type ContextProps = {
  operationId?: string;
  sessionId?: string;
  trackingAsset: (
    asset: string,
    documentType: AssetsType,
    stepId: string,
    stepType: string
  ) => void;
  trackingSignature: (value: TrackingSignatureValue) => void;
};

const Ctx = createContext<ContextProps>({
  operationId: undefined,
  sessionId: undefined,
  trackingAsset: () => {},
  trackingSignature: () => {},
});

export const ExtendedSdkProvider = ({ children }: IProps) => {
  const sdkProviderRef = useRef<HTMLFacephiSdkProviderElement>(null);
  const { sdkApiKey } = useVariables();
  const { getWorkflow } = useConfig();
  const { email, tenantDefault } = useAuth();
  const { i18n } = useTranslation();

  const [operationId, setOperationId] = useRecoilState(operationIdState);
  const [sessionId, setSessionId] = useRecoilState(sessionIdState);
  const newOperationId: string = useRecoilValue(newOperationIdState);
  const [flowOperationId, setFlowOperationId] = useState('');
  const [workflow, setWorkflow] = useState<WorkflowDTO>([]);
  const language = Object.values(Language).includes(i18n.language as Language)
    ? i18n.language
    : Language.en;

  const onEmitData = (event: FacephiSdkProviderCustomEvent<ExportData>) => {
    setSessionId(event.detail.sessionId);
    setOperationId(event.detail.operationId);
  };

  const trackingAsset = async (
    asset: string,
    documentType: AssetsType,
    stepId: string,
    stepType: string
  ) => {
    await sdkProviderRef.current?.trackingAsset(
      asset,
      documentType,
      stepId,
      stepType
    );
  };

  const trackingSignature = async (value: TrackingSignatureValue) => {
    await sdkProviderRef.current?.trackingSignature(value);
  };

  useEffect(() => {
    setFlowOperationId(newOperationId);
    setWorkflow(newOperationId ? getWorkflow() : []);
  }, [newOperationId]);

  return (
    <Ctx.Provider
      value={{
        operationId,
        sessionId,
        trackingAsset,
        trackingSignature,
      }}
    >
      {flowOperationId && workflow.length > 0 && (
        <FacephiSdkProvider
          bundlePath="/widgets"
          workflow={workflow}
          customerId={email}
          apikey={sdkApiKey}
          operationId={flowOperationId}
          language={language as Language}
          type={TypeFamily.onboarding}
          tenantId={tenantDefault}
          onEmitData={onEmitData}
          autoInitWorkflow={false}
          autoInitTracking={false}
          ref={sdkProviderRef}
          theme={{
            fontName: 'Poppins',
            logo: '',
            primaryColor: '#3167FC',
            secondaryColor: '#3167FC',
            tertiaryColor: '#2B50B6',
            backgroundColor: 'white',
          }}
          videoRecordingSse={true}
        >
          <>{children}</>
        </FacephiSdkProvider>
      )}
      {!flowOperationId && <>{children}</>}
    </Ctx.Provider>
  );
};

export const useSdk = () => useContext(Ctx);
